import type { ReactNode } from 'react'
import { Navbar } from '@/components/ui/navbar'
import { Footer } from '@/components/landing/footer'
import { Seo } from '@/components/seo/seo'

interface MarketingPageProps {
  readonly seoTitle: string
  readonly seoDescription: string
  readonly path: string
  /** Keyword-bearing eyebrow shown above the H1. */
  readonly eyebrow: string
  /** The page's single, unique <h1>. */
  readonly heading: string
  /** Lead paragraph rendered right after the H1 (front-loads the keyword). */
  readonly lead: string
  readonly children: ReactNode
}

/**
 * Shared shell for every public marketing content page: navbar, per-route SEO
 * head, a consistent hero (eyebrow + single H1 + lead), the page body, and the
 * landing footer. Pure presentation — no browser-API side effects at module
 * load, so it is safe under the puppeteer prerender.
 */
export function MarketingPage({
  seoTitle,
  seoDescription,
  path,
  eyebrow,
  heading,
  lead,
  children,
}: MarketingPageProps) {
  return (
    <div className="min-h-screen bg-background">
      <Seo title={seoTitle} description={seoDescription} path={path} />
      <Navbar />
      <main>
        <section className="mx-auto max-w-5xl px-6 pb-8 pt-16 sm:pt-24">
          <p className="text-sm font-semibold uppercase tracking-wide text-primary">{eyebrow}</p>
          <h1 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:text-6xl">
            {heading}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{lead}</p>
        </section>
        {children}
      </main>
      <Footer />
    </div>
  )
}
